import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';

interface HoverArea {
  id: string;
  title: string;
  description: string;
  x: number;
  y: number;
}

const hoverAreas: HoverArea[] = [
  {
    id: "sensors",
    title: "Sensors",
    description: "With wireless Modbus connectivity",
    x: 16,
    y: 52
  },
  {
    id: "controllers",
    title: "Room Controllers",
    description: "With integrated W-Modbus",
    x: 12,
    y: 20
  },
  {
    id: "actuators",
    title: "Actuators & Dampers",
    description: "With wireless W-Modbus integration",
    x: 64,
    y: 13
  },
  {
    id: "modbus-master",
    title: "Master controller",
    description: "With wireless Modbus control",
    x: 73.2,
    y: 87
  },
  {
    id: "submetering",
    title: "Submetering retrofit",
    description: "DIN rail and wall mounted wireless Modbus RTU",
    x: 35.4,
    y: 92
  }
];

const InteractiveBuilding = () => {
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const showRulers = false;

  const calculatePercentage = (clientX: number, clientY: number) => {
    if (!containerRef.current) return null;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;
    
    return {
      x: Math.round(x * 10) / 10,
      y: Math.round(y * 10) / 10
    };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!showRulers) return;
    const percentages = calculatePercentage(e.clientX, e.clientY);
    if (percentages) {
      setMousePos(percentages);
    }
  };

  const handleMouseLeave = () => {
    setMousePos(null);
  };

  const getTooltipPosition = (x: number): string => {
    if (x > 66) return 'right-full mr-2';
    if (x < 33) return 'left-full ml-2';
    return 'left-1/2 -translate-x-1/2';
  };

  return (
    <div className="relative">
      {showRulers && (
        <>
          {/* Vertical Scale */}
          <div className="absolute -left-8 top-0 h-full flex flex-col justify-between text-xs text-white">
            {Array.from({ length: 21 }, (_, i) => (
              <div key={`v-${i}`} className="flex items-center">
                <div className={`h-px w-2 bg-white/50 ${i % 2 === 0 ? 'w-3' : 'w-2'}`} />
                {i % 2 === 0 && <span className="ml-1">{100 - i * 5}%</span>}
              </div>
            ))}
          </div>

          {/* Horizontal Scale */}
          <div className="absolute -bottom-8 left-0 w-full flex justify-between text-xs text-white">
            {Array.from({ length: 21 }, (_, i) => (
              <div key={`h-${i}`} className="flex flex-col items-center">
                <div className={`w-px h-2 bg-white/50 ${i % 2 === 0 ? 'h-3' : 'h-2'}`} />
                {i % 2 === 0 && <span className="mt-1">{i * 5}%</span>}
              </div>
            ))}
          </div>
        </>
      )}

      <div 
        ref={containerRef}
        className="relative ml-[10%]"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <img 
          src="/lovable-uploads/66e67d58-8045-4778-b8ff-f4c7244e2829.png" 
          alt="Interactive Building" 
          className="w-full h-auto scale-x-[-1]"
        />
        
        {showRulers && mousePos && (
          <>
            {/* Vertical Guide Line */}
            <div 
              className="absolute top-0 w-px bg-white/50 pointer-events-none"
              style={{ 
                left: `${mousePos.x}%`,
                height: '100%'
              }}
            />
            
            {/* Horizontal Guide Line */}
            <div 
              className="absolute left-0 h-px bg-white/50 pointer-events-none"
              style={{ 
                top: `${mousePos.y}%`,
                width: '100%'
              }}
            />
            
            {/* Coordinate Dot and Label */}
            <motion.div 
              className="absolute w-2 h-2 bg-white rounded-full -translate-x-1 -translate-y-1 pointer-events-none"
              style={{ 
                left: `${mousePos.x}%`,
                top: `${mousePos.y}%`
              }}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.2 }}
            >
              <div className="absolute left-2 top-2 bg-black/80 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                x: {mousePos.x}%, y: {mousePos.y}%
              </div>
            </motion.div>
          </>
        )}
        
        {hoverAreas.map((area) => (
          <div
            key={area.id}
            className="hover-area absolute w-16 h-16 -translate-x-8 -translate-y-8 cursor-pointer group touch-none"
            style={{
              left: `${area.x}%`,
              top: `${area.y}%`,
              touchAction: 'manipulation'
            }}
          >
            <motion.div 
              className="absolute inset-0 bg-[#a6c522] bg-opacity-20 rounded-full"
              animate={{
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              animate={{
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <img 
                src="/lovable-uploads/741eb33e-fef3-4269-912b-11cb2276c97a.png" 
                alt="WiFi Icon"
                className="w-12 h-12"
              />
            </motion.div>
            <div 
              className={`absolute ${getTooltipPosition(area.x)} top-1/2 -translate-y-1/2 bg-white/90 p-3 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-56 z-10`}
            >
              <h3 className="text-gray-900 font-bold mb-2">{area.title}</h3>
              <p className="text-gray-700 text-sm">{area.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InteractiveBuilding;
